import React, { useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import googleMapsLogo from '../../assets/images/googleMapsLogo.png'
import wazeLogo from '../../assets/images/wazeLogo.png'
import { useClickOutside } from '../../core/application/hooks'
import { DestinationIcon } from '../icons'

export interface IGpsButtonProps {
  coordinate: {
    longitude: number
    latitude: number
  }
}

const GpsButton: React.FC<IGpsButtonProps> = ({ coordinate }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const overlayRef = useRef(null)

  const handleClickOutside = () => {
    if (isOpen) {
      setIsOpen((value) => !value)
    }
  }

  useClickOutside(overlayRef, handleClickOutside)

  const googleMapsUrl = `https://maps.google.com/?q=${coordinate.latitude},${coordinate.longitude}`
  const wazeUrl = `https://waze.com/ul?ll=${coordinate.latitude},${coordinate.longitude}`

  return (
    <Container data-testid="gpsButton-container" ref={overlayRef}>
      <Overlay data-testid="overlay" visible={isOpen}>
        <OverlayWrapper>
          <OverlayAction data-testid="google-overlay-action">
            <a href={encodeURI(googleMapsUrl)} target="_blank" rel="noreferrer">
              <OverlayIcon color="white">
                <img src={googleMapsLogo} alt="google maps logo" />
              </OverlayIcon>
              <span>
                <FormattedMessage id="DropOffDetails.GpsButton.GoogleMapsButton" />
              </span>
            </a>
          </OverlayAction>
          <OverlayAction data-testid="waze-overlay-action">
            <a href={encodeURI(wazeUrl)} target="_blank" rel="noreferrer">
              <OverlayIcon color="blue">
                <img src={wazeLogo} alt="waze logo" />
              </OverlayIcon>
              <span>
                <FormattedMessage id="DropOffDetails.GpsButton.WazeButton" />
              </span>
            </a>
          </OverlayAction>
        </OverlayWrapper>
      </Overlay>
      <ButtonWrapper data-testid="button" isOpen={isOpen} onClick={() => setIsOpen((value) => !value)}>
        <DestinationIcon customClass={isOpen ? 'white' : 'black'} />
      </ButtonWrapper>
    </Container>
  )
}

interface WithIsOpen {
  isOpen: boolean
}

const Container = styled.div`
  position: relative;
`

interface IOverlayProps {
  visible: boolean
}

const Overlay = styled.div<IOverlayProps>`
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
  position: absolute;
  background-color: ${({ theme }) => theme.color.black};
  bottom: 43px;
  padding: 12px;
  border-radius: 4px;
  width: 134px;
  left: 0px;

  z-index: 15;

  opacity: ${({ visible }) => (visible ? '1' : '0')};
  transition: opacity 300ms ease-out;
`

const OverlayWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
`

const OverlayAction = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 8px;

  a {
    display: flex;
    flex-direction: row;
    align-items: center;
    column-gap: 8px;
    font-size: ${({ theme }) => theme.size.smaller};
    color: ${({ theme }) => theme.color.white};
    text-decoration: none;
  }
`

interface IOverlayIconProps {
  color: 'white' | 'blue'
}

const OverlayIcon = styled.div<IOverlayIconProps>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${({ theme, color }) => (color === 'white' ? theme.color.white : '#00D0FF')};
  border: 1px solid ${({ theme, color }) => (color === 'white' ? theme.color.white : '#00D0FF')};
`

const ButtonWrapper = styled.button<WithIsOpen>`
  background-color: ${({ theme, isOpen }) => (isOpen ? theme.color.black : theme.color.lightGrey)};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  column-gap: 8px;
  padding: 8px 16px;

  cursor: pointer;

  font-size: ${({ theme }) => theme.size.small};
  font-family: ${({ theme }) => theme.font.semi};
  color: ${({ theme, isOpen }) => (isOpen ? theme.color.white : theme.color.black)};
  font-weight: ${({ theme }) => theme.weight.semi};

  transition: background-color 300ms ease-out, color 300ms ease-out;
`

export default GpsButton
